import { FC, useEffect, useMemo, useState } from "react";
import "./paymentLinkModal.css";
import CloseIcon from "@mui/icons-material/Close";
import LockIcon from "@mui/icons-material/Lock";
import CircularProgress from "@mui/material/CircularProgress";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import useCreatePaymentLink from "../../service/createPaymentLink/createPaymentLink";

interface PaymentLinkModalProps {
  open: boolean;
  onClose: () => void;
}

const PaymentLinkModal: FC<PaymentLinkModalProps> = ({ open, onClose }) => {
  const [concept, setConcept] = useState("");
  const [amount, setAmount] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [createdLink, setCreatedLink] = useState("");
  const [showResultModal, setShowResultModal] = useState(false);
  const [copyOk, setCopyOk] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const { createPaymentLink, loading, error, reset } = useCreatePaymentLink();

  const parsedAmount = useMemo(() => Number(amount), [amount]);
  const isValidEmail = useMemo(() => /^\S+@\S+\.\S+$/.test(email.trim()), [email]);
  const hasAmountRangeError = useMemo(() => {
    if (!amount) return false;
    if (Number.isNaN(parsedAmount)) return true;
    return parsedAmount < 300;
  }, [amount, parsedAmount]);
  const submitDisabled =
    loading ||
    !concept.trim() ||
    !name.trim() ||
    !email.trim() ||
    !isValidEmail ||
    !amount ||
    Number.isNaN(parsedAmount) ||
    parsedAmount < 300;

  useEffect(() => {
    if (!open) {
      setConcept("");
      setAmount("");
      setName("");
      setEmail("");
      setCreatedLink("");
      setShowResultModal(false);
      setCopyOk(false);
      setSubmitError("");
      reset();
    }
  }, [open, reset]);

  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  const handleGenerateLink = async () => {
    if (submitDisabled) return;
    setSubmitError("");

    try {
      const response = await createPaymentLink({
        description: concept.trim(),
        amount: parsedAmount,
        name: name.trim(),
        email: email.trim(),
        redirect_url: "https://www.pagosfiable.com",
      });

      const url = response?.payment_method?.url;

      if (!url) {
        setSubmitError("No se recibió un link válido en la respuesta");
        return;
      }

      setCreatedLink(url);
      setShowResultModal(true);
    } catch (err: any) {
      setSubmitError(err?.message || "No fue posible generar el link de pago");
    }
  };

  const handleCopyLink = async () => {
    if (!createdLink) return;

    try {
      await navigator.clipboard.writeText(createdLink);
      setCopyOk(true);
      setTimeout(() => setCopyOk(false), 1800);
    } catch {
      setSubmitError("No se pudo copiar el link. Intenta copiarlo manualmente.");
    }
  };

  if (!open) return null;

  if (showResultModal) {
    return (
      <div className="payment-link-overlay" onClick={handleClose}>
        <div className="payment-link-modal payment-link-modal-result" onClick={(e) => e.stopPropagation()}>
          <div className="payment-link-modal-header">
            <div>
              <h2 className="payment-link-modal-title">Link generado</h2>
              <p className="payment-link-modal-subtitle">
                Comparte este enlace con tu cliente para recibir el pago.
              </p>
            </div>
            <button className="payment-link-close-btn" onClick={handleClose}>
              <CloseIcon fontSize="small" />
            </button>
          </div>

          <div className="payment-link-result-box">
            <CheckCircleOutlineIcon className="payment-link-result-icon" />
            <p className="payment-link-result-label">URL de pago</p>
            <p className="payment-link-result-url">{createdLink}</p>
          </div>

          <div className="payment-link-modal-footer">
            <button className="payment-link-cancel-btn" onClick={handleClose}>
              Cerrar
            </button>
            <button className="payment-link-submit-btn" onClick={handleCopyLink}>
              <ContentCopyIcon fontSize="small" />
              {copyOk ? "Copiado" : "Copiar link"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-link-overlay" onClick={handleClose}>
      <div className="payment-link-modal" onClick={(e) => e.stopPropagation()}>
        <div className="payment-link-modal-header">
          <div>
            <h2 className="payment-link-modal-title">Crear nuevo link de pago</h2>
            <p className="payment-link-modal-subtitle">
              Configura los detalles para generar un nuevo enlace de cobro seguro.
            </p>
          </div>
          <button className="payment-link-close-btn" onClick={handleClose} disabled={loading}>
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <div className="payment-link-modal-body">
          <div className="payment-link-field">
            <label className="payment-link-label">Concepto / Descripción del pago</label>
            <input
              type="text"
              className="payment-link-input"
              placeholder="Ej. Pago de servicios de consultoría mensual"
              value={concept}
              onChange={(e) => setConcept(e.target.value)}
            />
          </div>

          <div className="payment-link-field">
            <label className="payment-link-label">Nombre de la persona</label>
            <input
              type="text"
              className="payment-link-input"
              placeholder="Ej. Juan Vázquez"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="payment-link-field">
            <label className="payment-link-label">Correo electrónico</label>
            <input
              type="email"
              className="payment-link-input"
              placeholder="Ej. cliente@correo.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="payment-link-field payment-link-field-amount">
            <label className="payment-link-label">Monto (MXN)</label>
            <div className="payment-link-input-prefix">
              <span className="payment-link-prefix">$</span>
              <input
                type="number"
                className="payment-link-input payment-link-input-no-border"
                placeholder="0.00"
                value={amount}
                min="300"
                step="0.01"
                onChange={(e) => setAmount(e.target.value)}
              />
            </div>
            {hasAmountRangeError && (
              <p className="payment-link-error">El monto debe ser mayor o igual a 300.</p>
            )}
          </div>
        </div>

        {(submitError || error) && <p className="payment-link-error">{submitError || error}</p>}

        <div className="payment-link-modal-footer">
          <button className="payment-link-cancel-btn" onClick={handleClose} disabled={loading}>
            Cancelar
          </button>
          <button
            className="payment-link-submit-btn"
            onClick={handleGenerateLink}
            disabled={submitDisabled}
          >
            {loading ? <CircularProgress size={16} color="inherit" /> : <LockIcon fontSize="small" />}
            {loading ? "Generando..." : "Generar Link"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentLinkModal;
